/**
 * usePackerWebSocket — WebSocket event listeners for MobilePackerView.
 * Handles: config-updated, weight_mode_updated, status_updated,
 * worker-clocked-out (multi-device sync), pending-label-voided.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../../../i18n/LanguageContext';
import { wsService } from '../../../services/websocket';
import productionService from '../../../services/productionService';
import { clearMobileState } from '../../../utils/mobileSessionStorage';
import type { SessionInfo } from '../../../types';

interface UsePackerWebSocketParams {
  session: SessionInfo | null;
  setSession: React.Dispatch<React.SetStateAction<SessionInfo | null>>;
  employee: { employee_number: string } | null;
  station: { printer_mac_id?: string; station_id?: number } | null;
  isLocalClockOutRef: React.MutableRefObject<boolean>;
  mountTimeRef: React.MutableRefObject<number>;
  setStep: (step: 'weight_entry' | 'printing' | 'scan_confirm' | 'success') => void;
  setWeight: (w: string) => void;
  setLastPrintedBarcode: (b: string | null) => void;
  setScanStep: (s: 'badge' | 'label') => void;
  setSubUnitsPacked: (n: number) => void;
  setBoxCount: React.Dispatch<React.SetStateAction<number>>;
  setNumberOfCopies: (n: number) => void;
  setPrinterMac: (mac: string | null) => void;
  setPrinterIp: (ip: string | null) => void;
  setRequirePostPrintScan: (v: boolean) => void;
  setSimulatePrinter: (v: boolean) => void;
  setDefaultWeightMode: (mode: 'fixed' | 'catch' | 'scale') => void;
  setWeightTolerance: (v: number) => void;
  setBagTareEnabled: (v: boolean) => void;
  setBagTareWeight: (v: number) => void;
  setCaseTareEnabled: (v: boolean) => void;
  setCaseTareWeight: (v: number) => void;
}

export function usePackerWebSocket({
  session,
  setSession,
  employee,
  station,
  isLocalClockOutRef,
  mountTimeRef,
  setStep,
  setWeight,
  setLastPrintedBarcode,
  setScanStep,
  setSubUnitsPacked,
  setBoxCount,
  setNumberOfCopies,
  setPrinterMac,
  setPrinterIp,
  setRequirePostPrintScan,
  setSimulatePrinter,
  setDefaultWeightMode,
  setWeightTolerance,
  setBagTareEnabled,
  setBagTareWeight,
  setCaseTareEnabled,
  setCaseTareWeight,
}: UsePackerWebSocketParams) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Load config settings and listen for real-time config updates
  useEffect(() => {
    productionService.getConfigSettings()
      .then(settings => {
        const lc = settings.find((s: any) => s.setting_key === 'label_copies');
        if (lc) setNumberOfCopies(Number(lc.setting_value) || 2);
        const pps = settings.find((s: any) => s.setting_key === 'require_post_print_scan');
        if (pps) setRequirePostPrintScan(pps.setting_value === 'true');
        const sp = settings.find((s: any) => s.setting_key === 'simulate_printer');
        if (sp) setSimulatePrinter(sp.setting_value === 'true');
        const dwm = settings.find((s: any) => s.setting_key === 'default_weight_mode');
        if (dwm && ['fixed', 'catch', 'scale'].includes(dwm.setting_value)) {
          setDefaultWeightMode(dwm.setting_value as 'fixed' | 'catch' | 'scale');
        }
        const wt = settings.find((s: any) => s.setting_key === 'weight_tolerance');
        if (wt) setWeightTolerance(parseFloat(wt.setting_value) || 0.05);
        const bte = settings.find((s: any) => s.setting_key === 'bag_tare_enabled');
        if (bte) setBagTareEnabled(bte.setting_value === 'true');
        const btw = settings.find((s: any) => s.setting_key === 'bag_tare_weight');
        if (btw) setBagTareWeight(parseFloat(btw.setting_value) || 0.05);
        const cte = settings.find((s: any) => s.setting_key === 'case_tare_enabled');
        if (cte) setCaseTareEnabled(cte.setting_value === 'true');
        const ctw = settings.find((s: any) => s.setting_key === 'case_tare_weight');
        if (ctw) setCaseTareWeight(parseFloat(ctw.setting_value) || 1.35);

        // Resolve printer MAC from station's printer_mac_id config key
        if (station?.printer_mac_id) {
          const printerEntry = settings.find((s: any) => s.setting_key === station.printer_mac_id);
          if (printerEntry?.setting_value) {
            setPrinterMac(printerEntry.setting_value);
          }
          const ipEntry = settings.find((s: any) => s.setting_key === station.printer_mac_id + '_ip');
          if (ipEntry?.setting_value) {
            setPrinterIp(ipEntry.setting_value);
          }
        }
      })
      .catch(() => { /* keep env/default fallback */ });

    wsService.connect();
    const handleConfigUpdate = (data: any) => {
      if (data?.key === 'label_copies') {
        setNumberOfCopies(Number(data.value) || 2);
      }
      if (data?.key === 'require_post_print_scan') {
        setRequirePostPrintScan(data.value === 'true');
      }
      if (data?.key === 'default_weight_mode' && ['fixed', 'catch', 'scale'].includes(data.value)) {
        setDefaultWeightMode(data.value as 'fixed' | 'catch' | 'scale');
      }
      if (data?.key === 'weight_tolerance') {
        setWeightTolerance(parseFloat(data.value) || 0.05);
      }
      if (data?.key === 'simulate_printer') {
        setSimulatePrinter(data.value === 'true');
      }
      if (data?.key === 'bag_tare_enabled') {
        setBagTareEnabled(data.value === 'true');
      }
      if (data?.key === 'bag_tare_weight') {
        setBagTareWeight(parseFloat(data.value) || 0.05);
      }
      if (data?.key === 'case_tare_enabled') {
        setCaseTareEnabled(data.value === 'true');
      }
      if (data?.key === 'case_tare_weight') {
        setCaseTareWeight(parseFloat(data.value) || 1.35);
      }
    };
    wsService.on('config-updated', handleConfigUpdate);
    return () => { wsService.off('config-updated', handleConfigUpdate); };
  }, []);

  // Listen for session updates, clock-out events, and pending label voids
  useEffect(() => {
    if (!session?.sub_session_number) return;

    wsService.connect();

    const handleSessionUpdated = (data: { sub_session_number?: string; action?: string; weight_mode?: 'fixed' | 'catch' | 'scale'; status?: string }) => {
      if (data.sub_session_number !== session.sub_session_number) return;

      if (data.action === 'weight_mode_updated' && data.weight_mode) {
        setSession(prev => prev ? { ...prev, weight_mode: data.weight_mode } : prev);
        const modeName = data.weight_mode === 'fixed'
          ? (t('sessionDetails.fixedWeight') || 'Fixed')
          : data.weight_mode === 'scale'
            ? (t('mobile.scaleWeight') || 'Scale')
            : (t('sessionDetails.catchWeight') || 'Catch');
        toast.success(`${t('operator.weightModeChanged')} ${modeName}`);
      }

      if (data.action === 'status_updated' && data.status) {
        setSession(prev => prev ? { ...prev, status: data.status! } : prev);
        if (data.status === 'completed') {
          toast.success(t('mobile.sessionCompletedBySupervisor') || 'Session has been completed');
        }
      }
    };

    wsService.on('production-session-updated', handleSessionUpdated);

    const handleWorkerClockedOut = (data: {
      sub_session_number?: string;
      employee_number?: string;
      role?: string;
      station_id?: number;
      reason?: string;
      relieved_by?: string;
    }) => {
      if (data.employee_number === employee?.employee_number) {
        if (isLocalClockOutRef.current) {
          isLocalClockOutRef.current = false;
          return;
        }

        if (data.reason === 'new_device_login') {
          const timeSinceMount = Date.now() - mountTimeRef.current;
          if (timeSinceMount < 3000) {
            return;
          }
        }

        if (data.reason === 'relieved') {
          toast.error(t('mobile.youHaveBeenRelieved') || `You have been relieved by #${data.relieved_by || 'another worker'}`);
        } else if (data.reason === 'new_device_login') {
          toast.error(t('mobile.loggedInFromAnotherDevice') || 'You have logged in from another device');
        } else {
          toast.error(t('mobile.clockedOutFromAnotherDevice') || 'You have been clocked out from another device');
        }

        clearMobileState();
        navigate('/mobile/clock-in');
      }
    };

    wsService.on('worker-clocked-out', handleWorkerClockedOut);

    const handlePendingLabelVoided = (data: {
      box_id?: string | string;
      sub_session_number?: string;
      session_number?: string;
    }) => {
      if (data.sub_session_number === session?.sub_session_number || data.session_number === session?.session_number) {
        toast(t('mobile.pendingLabelVoided') || 'Pending label was voided by supervisor');
        setStep('weight_entry');
        setWeight('');
        setLastPrintedBarcode(null);
        setScanStep('badge');
        setSubUnitsPacked(0);
      }
    };

    wsService.on('pending-label-voided', handlePendingLabelVoided);

    // Listen for box-packed events from other devices to keep box count in sync
    const handleBoxPacked = (data: {
      sub_session_number?: string;
      session_number?: string;
      employee_number?: string;
      total_session_boxes?: number;
      status?: string;
    }) => {
      if (data.status !== 'packed') return;
      if (data.sub_session_number !== session?.sub_session_number &&
          data.session_number !== session?.session_number) return;
      // If the server sent a total, use it directly; otherwise increment
      if (typeof data.total_session_boxes === 'number') {
        setBoxCount(data.total_session_boxes);
      } else {
        setBoxCount(prev => prev + 1);
      }
    };

    wsService.on('box-packed', handleBoxPacked);

    return () => {
      wsService.off('production-session-updated', handleSessionUpdated);
      wsService.off('worker-clocked-out', handleWorkerClockedOut);
      wsService.off('pending-label-voided', handlePendingLabelVoided);
      wsService.off('box-packed', handleBoxPacked);
    };
  }, [session?.sub_session_number, session?.session_number, employee?.employee_number, t, navigate]);
}
